/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ChangeDetectionStrategy,
  AfterViewInit,
  Component,
  Input,
  OnDestroy,
  OnInit,
  TrackByFunction,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { debouncedSearch } from '@vsolv/utils/rxjs-operators';
import {
  BadgeColumn,
  PaginationConfig,
  SortableTableComponent,
  SortParams,
  TableColumn,
  TextColumn,
} from '@vsolv/vectors/table';
import { Distributor } from '@wsphere/distributors/domain';
import { SecurityService, StaffService } from '@wsphere/staff/web';
import moment from 'moment';
import {
  BehaviorSubject,
  combineLatest,
  EMPTY,
  firstValueFrom,
  from,
  map,
  Observable,
  ReplaySubject,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { DISTRIBUTOR_TABLE_CONFIG } from '../../config/distributor-table.config';
import { CreateDistributorDialog } from '../../dialogs';
import { DistributorService } from '../../services';
import { DistributorNameCellState, DistributorNameColumn } from '../distributor-name-column';

export type DistributorTableFilters = Pick<
  Distributor.ListDistributorsQueryRequest,
  'search' | 'status' | 'includeSubDistributors'
>;

export type DistributorTreeRow = Distributor.Model & {
  /** Depth of rows inserted by expanding a parent. */
  _treeLevel?: number;
  /** Parent name for the badge, set when a row is inserted under its parent. */
  _treeParentName?: string;
};

/** One request per expanded parent. */
const CHILD_FETCH_LIMIT = 100;

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-distributor-table',
  templateUrl: './distributor-table.component.html',
  standalone: false,
})
export class DistributorTableComponent extends SortableTableComponent implements OnInit, AfterViewInit, OnDestroy {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private staffSvc: StaffService,
    private securitySvc: SecurityService,
    private distributorSvc: DistributorService
  ) {
    super();
  }

  @ViewChild('addDistributorDialog') addDistributorDialog?: CreateDistributorDialog;

  _distributor?: Distributor.Model;
  @Input() set distributor(distributor: Distributor.Model) {
    if (distributor) {
      if (distributor.id !== this._distributor?.id) {
        this.distributorCount = 0;
        this.isInitialLoading = true;
      }
      this._distributor = distributor;
    }
    this.refresh$.next(null);
  }

  @Input() onSettings = false;

  @PropertyListener('distributor') private distributor$ = new ReplaySubject<Distributor.Model | undefined>(undefined);
  staff$ = from(this.staffSvc.retrieveSelf()).pipe(shareReplay(1));

  readonly canCreateDistributor$: Observable<boolean> = combineLatest([this.distributor$, this.staff$]).pipe(
    switchMap(async ([distributor]) => {
      const permissionKeys = this.securitySvc.globalDistributors$.value?.map(dist => dist.permissionKey) ?? null;

      return await this.securitySvc.hasAccess(
        'dist_CreateDistributor',
        distributor?.permissionKey ? [distributor.permissionKey] : permissionKeys
      );
    })
  );

  readonly canManageAccountInfo$: Observable<boolean> = combineLatest([this.distributor$, this.staff$]).pipe(
    switchMap(async ([distributor]) => {
      const permissionKeys = this.securitySvc.globalDistributors$.value?.map(dist => dist.permissionKey) ?? null;

      return await this.securitySvc.hasAccess(
        'dist_ManageAccountInfo',
        distributor?.permissionKey ? [distributor.permissionKey] : permissionKeys
      );
    })
  );

  readonly config = DISTRIBUTOR_TABLE_CONFIG;

  distributorCount = 0;
  isInitialLoading = true;
  statuses = Object.keys(Distributor.Status);
  active = Distributor.Status.ACTIVE;

  private refresh$ = new BehaviorSubject<null>(null);
  readonly searchInput$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  filters: DistributorTableFilters | null = null;
  private filters$ = new BehaviorSubject(this.filters);

  override onSortChanged(sortData: SortParams) {
    super.onSortChanged(sortData);
    this.pagination$.next({ ...this.pagination$.value, currentPage: 1 });
  }

  readonly pagination$ = new BehaviorSubject({ currentPage: 1, itemsPerPage: 10 });
  readonly itemsPerPage = this.pagination$.value.itemsPerPage;

  private paginatedData$ = combineLatest([
    this.pagination$,
    this.filters$,
    this.securitySvc.globalDistributors$,
    this.refresh$,
  ]).pipe(
    switchMap(([pagination, filters]) => {
      // Bump on request so a running cascade stops even if the fetch fails
      this.treeEpoch++;
      const sort = this.sort$.value;
      return this.distributorSvc
        .listDistributors$({
          page: pagination.currentPage,
          limit: pagination.itemsPerPage,
          search: filters?.search,
          status: filters?.status,
          // Search looks through the whole hierarchy; the toggle expands the tree client-side
          includeSubDistributors: this._distributor?.id || filters?.search ? true : undefined,
          distributorIds: this._distributor?.id ? [this._distributor.id] : [],
          ...(sort && { sortBy: sort.sortBy, sortOrder: sort.sortOrder }),
        })
        .pipe(
          catchError(() => {
            this.isInitialLoading = false;
            return EMPTY;
          })
        );
    }),
    tap(data => {
      if (this.distributorCount === 0) this.distributorCount = data?.meta?.totalItems || 0;
      this.isInitialLoading = false;
      // Every fetch rebuilds the tree from scratch
      this.subCountCache.clear();
      this.cascadeOptOut.clear();
      this.treeState$.next({ expanded: new Set(), loading: new Set() });
      this.treeRows$.next(data.items ?? []);
      if (!this._distributor?.id && this.filters?.includeSubDistributors && !this.filters?.search) {
        void this.expandAllTree();
      }
    }),
    shareReplay(1)
  );

  private readonly treeRows$ = new BehaviorSubject<DistributorTreeRow[]>([]);
  private readonly treeState$ = new BehaviorSubject<{ expanded: ReadonlySet<string>; loading: ReadonlySet<string> }>({
    expanded: new Set(),
    loading: new Set(),
  });
  private readonly subCountCache = new Map<string, Promise<number>>();
  /** Bumped on every refetch so stale expansions get discarded. */
  private treeEpoch = 0;
  /** Rows the user collapsed — expand-all leaves them alone. */
  private readonly cascadeOptOut = new Set<string>();

  readonly distributors$: Observable<DistributorTreeRow[]> = this.paginatedData$.pipe(
    switchMap(() => this.treeRows$),
    startWith([] as DistributorTreeRow[])
  );

  readonly trackById: TrackByFunction<DistributorTreeRow> = (index, row) => row.id || index;

  /** Tint for expanded child rows. */
  readonly treeRowClasses = (row: DistributorTreeRow) =>
    row._treeLevel ? '!bg-gray-100 group-hover:!bg-gray-200' : '';

  readonly paginationConfig$: Observable<Partial<PaginationConfig>> = this.paginatedData$.pipe(
    map(data => data.meta),
    startWith(this.pagination$.value)
  );

  columns: TableColumn<unknown>[] = [
    new DistributorNameColumn<DistributorTreeRow>(
      // min-w keeps names readable on deeply indented rows
      { header: 'Distributor', sortable: true, customCellClasses: 'min-w-[280px]' },
      distributor => ({
        text: distributor.name,
        classes: !distributor.id ? 'text-md font-semibold' : '',
        level: distributor._treeLevel ?? 0,
        isSub: !this._distributor?.id && !!distributor.parentId,
        parentName:
          distributor._treeParentName ??
          (distributor.parentId ? this.treeRows$.value.find(row => row.id === distributor.parentId)?.name : null),
        // No expanding inside search results — they're a flat list
        state$: this.treeEnabled() && distributor.id && !this.filters?.search ? this.rowTreeState$(distributor) : null,
        toggled: () => void this.toggleExpand(distributor),
      })
    ),

    new TextColumn<Distributor.Model>(
      { header: 'No. of Members', align: 'center', fitContent: true },
      async distributor => {
        const staff = await this.staffSvc.count({ permissionKey: distributor.permissionKey, includeChildren: true });
        const count = staff?.count || 0;
        return { text: count + '' };
      }
    ),

    new TextColumn<Distributor.Model>(
      { header: 'No. of Sub-distributors', align: 'center', fitContent: true },
      async distributor => {
        const count = await this.subDistributorCount(distributor);
        return { text: count + '' };
      }
    ),

    new BadgeColumn<Distributor.Model>({ header: 'Status', sortable: true }, distributor => ({
      text: distributor.status.charAt(0) + distributor.status.slice(1).toLowerCase(),
      displayStatusIcon: true,
      theme: this.getTheme(distributor.status),
    })),

    new TextColumn<Distributor.Model>({ header: 'Last activity', sortable: true }, distributor => ({
      text: moment(distributor.modified as Date).format('MMM DD, YYYY'),
    })),

    new TextColumn<Distributor.Model>(
      { header: DISTRIBUTOR_TABLE_CONFIG.lastAccessColumn, sortable: true },
      distributor => ({
        // Green dot marks a real sign-in; fallback dates render muted
        text:
          (distributor.lastAccessedAt
            ? '<span class="inline-block w-1.5 h-1.5 rounded-full bg-success-500 mr-1.5 align-middle"></span>'
            : '') + moment((distributor.lastAccessedAt ?? distributor.modified) as Date).format('MMM DD, YYYY'),
        ...(!distributor.lastAccessedAt && {
          classes: 'text-gray-400',
          tooltip: DISTRIBUTOR_TABLE_CONFIG.lastAccessFallbackTooltip,
        }),
      })
    ),

    new TextColumn<Distributor.Model>({ header: 'Date created', sortable: true }, distributor => ({
      text: moment(distributor.created as Date).format('MMM DD, YYYY'),
    })),
  ];

  /** Only the top-level list is expandable. */
  private treeEnabled(): boolean {
    return !this._distributor?.id;
  }

  /** Expand every row that has children, depth first. */
  private async expandAllTree() {
    const epoch = this.treeEpoch;
    // Walk by id — collapsing rows mid-walk would shift indexes
    const queue = this.treeRows$.value.map(row => row.id).filter(Boolean);

    while (queue.length) {
      if (epoch !== this.treeEpoch) return;
      const id = queue.shift() as string;
      const row = this.treeRows$.value.find(candidate => candidate.id === id);
      if (!row || this.cascadeOptOut.has(id)) continue;

      const count = await this.subDistributorCount(row).catch(() => 0);
      if (epoch !== this.treeEpoch) return;
      if (count <= 0 || this.treeState$.value.expanded.has(id) || this.cascadeOptOut.has(id)) continue;

      const before = new Set(this.treeRows$.value.map(candidate => candidate.id));
      await this.toggleExpand(row);
      if (epoch !== this.treeEpoch) return;

      // New children go to the front to keep the walk depth-first
      queue.unshift(...this.treeRows$.value.filter(candidate => !before.has(candidate.id)).map(child => child.id));
    }
  }

  /** Shared by the count column and the expand toggle. */
  private subDistributorCount(distributor: Distributor.Model): Promise<number> {
    let count = this.subCountCache.get(distributor.id);
    if (!count) {
      count = Promise.resolve(this.distributorSvc.count(distributor.permissionKey)).then(distributors =>
        // Reduce by one to remove self from count
        distributors?.count ? distributors.count - 1 || 0 : 0
      );
      this.subCountCache.set(distributor.id, count);
      // Drop failed lookups so the next render can retry
      count.catch(() => {
        if (this.subCountCache.get(distributor.id) === count) this.subCountCache.delete(distributor.id);
      });
    }
    return count;
  }

  private rowTreeState$(distributor: DistributorTreeRow): Observable<DistributorNameCellState> {
    // Emit right away so the toggle space is reserved before the count loads
    return combineLatest([
      from(this.subDistributorCount(distributor).catch(() => 0)).pipe(startWith(0)),
      this.treeState$,
    ]).pipe(
      map(([count, state]) => ({
        expandable: count > 0,
        expanded: state.expanded.has(distributor.id),
        loading: state.loading.has(distributor.id),
      }))
    );
  }

  async toggleExpand(distributor: DistributorTreeRow) {
    const { expanded, loading } = this.treeState$.value;
    if (loading.has(distributor.id)) return;

    if (expanded.has(distributor.id)) {
      this.collapseRow(distributor);
      return;
    }

    this.treeState$.next({ expanded, loading: new Set(loading).add(distributor.id) });
    const epoch = this.treeEpoch;

    const children = await firstValueFrom(
      this.distributorSvc.listDistributors$({
        page: 1,
        limit: CHILD_FETCH_LIMIT,
        status: this.filters?.status,
        includeSubDistributors: true,
        distributorIds: [distributor.id],
        ...(this.sort$.value && { sortBy: this.sort$.value.sortBy, sortOrder: this.sort$.value.sortOrder }),
      })
    ).catch(() => null);

    // The list changed while we were loading — drop these children
    if (epoch !== this.treeEpoch) return;

    const nextLoading = new Set(this.treeState$.value.loading);
    nextLoading.delete(distributor.id);

    const rows = [...this.treeRows$.value];
    const index = rows.findIndex(row => row.id === distributor.id);
    // Nothing visible to show (archived/filtered) — stay collapsed
    if (!children?.items.length || index === -1) {
      this.treeState$.next({ expanded: this.treeState$.value.expanded, loading: nextLoading });
      return;
    }

    const present = new Set(rows.map(row => row.id));
    const level = (distributor._treeLevel ?? 0) + 1;
    const inserted = children.items
      .filter(child => !present.has(child.id))
      .map(child => ({ ...child, _treeLevel: level, _treeParentName: distributor.name }));

    rows.splice(index + 1, 0, ...inserted);
    this.treeRows$.next(rows);
    this.treeState$.next({
      expanded: new Set(this.treeState$.value.expanded).add(distributor.id),
      loading: nextLoading,
    });
  }

  private collapseRow(distributor: DistributorTreeRow) {
    // Remember the collapse so expand-all doesn't reopen it
    this.cascadeOptOut.add(distributor.id);
    // Descendants share the permissionKey prefix
    const keyById = new Map(this.treeRows$.value.map(row => [row.id, row.permissionKey]));
    const rows = this.treeRows$.value.filter(
      row => !row._treeLevel || row.id === distributor.id || !row.permissionKey?.startsWith(distributor.permissionKey)
    );
    const kept = new Set(rows.map(row => row.id));
    // Their expansions go too
    const expanded = new Set(
      [...this.treeState$.value.expanded].filter(
        id => id !== distributor.id && kept.has(id) && !keyById.get(id)?.startsWith(distributor.permissionKey)
      )
    );

    this.treeRows$.next(rows);
    this.treeState$.next({ expanded, loading: this.treeState$.value.loading });
  }

  getTheme(status: Distributor.Status) {
    switch (status) {
      case Distributor.Status.ACTIVE:
        return 'success';
      case Distributor.Status.DEACTIVATED:
        return 'info';
      default:
        return 'default';
    }
  }

  openAddDistributorDialog() {
    this.addDistributorDialog?.openDialog();
  }

  async refreshTable(filters?: { search?: string; status?: Distributor.Status; includeSubDistributors?: boolean }) {
    if (filters?.search !== undefined) this.filters = { ...this.filters, search: filters.search };
    if (filters && 'status' in filters) {
      if (filters.status) {
        this.filters = { ...(this.filters ?? {}), status: filters.status };
      } else if (this.filters?.status) {
        const nextFilters = { ...this.filters };
        delete nextFilters.status;
        this.filters = Object.keys(nextFilters).length ? nextFilters : null;
      }
    }
    if (filters?.includeSubDistributors !== undefined)
      this.filters = { ...this.filters, includeSubDistributors: filters.includeSubDistributors };
    // New filters shrink the results — go back to page 1
    if (this.pagination$.value.currentPage !== 1) {
      this.pagination$.next({ ...this.pagination$.value, currentPage: 1 });
    }
    this.filters$.next(this.filters);
    this.syncFiltersToUrl();
  }

  onStatusChange(status: Distributor.Status | readonly Distributor.Status[] | null) {
    const resolvedStatus = Array.isArray(status) ? (status[0] ?? null) : status;
    const selectedStatus = this.filters?.status ?? null;
    const nextStatus = selectedStatus === resolvedStatus ? null : resolvedStatus;
    this.refreshTable({ status: nextStatus ?? undefined });
  }

  ngOnInit(): void {
    if (this._distributor?.id) return;
    const urlFilters = this.getFiltersFromUrl();
    if (Object.keys(urlFilters).length) {
      void this.refreshTable(urlFilters);
    }
  }

  ngOnDestroy(): void {
    // Stops any in-flight expand cascade
    this.treeEpoch++;
    this.destroy$.next();
    this.destroy$.complete();
    this.treeRows$.complete();
    this.treeState$.complete();
  }

  ngAfterViewInit(): void {
    this.searchInput$
      .pipe(
        debouncedSearch(300),
        tap(search => this.refreshTable({ search })),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  navigateTo(distributorId: string) {
    if (this._distributor?.id) {
      this.router.navigate([this.onSettings ? `../../distributors/${distributorId}` : `../${distributorId}`], {
        relativeTo: this.route,
      });
    } else this.router.navigate([`${distributorId}`], { relativeTo: this.route });
  }

  private syncFiltersToUrl() {
    // Nested sub-distributor tables should not overwrite the top-level list URL
    if (this._distributor?.id) return;

    const search = this.filters?.search || null;
    const status = this.filters?.status ?? null;
    const includeSubDistributors = this.filters?.includeSubDistributors ? 'true' : null;
    const params = this.route.snapshot.queryParamMap;

    if (
      (params.get('search') || null) === search &&
      (params.get('status') || null) === status &&
      (params.get('includeSubDistributors') || null) === includeSubDistributors
    ) {
      return;
    }

    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { search, status, includeSubDistributors },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private getFiltersFromUrl(): {
    search?: string;
    status?: Distributor.Status;
    includeSubDistributors?: boolean;
  } {
    const params = this.route.snapshot.queryParamMap;
    const search = params.get('search') || undefined;
    const statusParam = params.get('status');
    const status =
      statusParam && (Object.values(Distributor.Status) as string[]).includes(statusParam)
        ? (statusParam as Distributor.Status)
        : undefined;
    const includeSubDistributors = params.get('includeSubDistributors') === 'true' ? true : undefined;

    return {
      ...(search !== undefined ? { search } : {}),
      ...(status !== undefined ? { status } : {}),
      ...(includeSubDistributors !== undefined ? { includeSubDistributors } : {}),
    };
  }
}
